import React, { useRef } from "react";
import { motion, useInView, useAnimation } from "framer-motion";
import { Link } from "react-router-dom";
import HeroSection from "../../components/HeroSection";
import { FaArrowRight, FaGraduationCap, FaHeartbeat, FaRoad, FaBuilding } from "react-icons/fa";
import roadConstruction1 from "../../assets/gallery/community-projects/road-construction1.jpeg";
import groupEventAsset from "../../assets/gallery/events/groupix.png";

const AnimatedSection = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const controls = useAnimation();

  if (isInView) {
    controls.start("visible");
  }

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0 }
      }}
      transition={{ duration: 0.6, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export default function OurPartners() {
  const groupEvent = `${groupEventAsset}?v=20260424`;
  const partnerCategories = [
    {
      icon: <FaGraduationCap className="text-3xl text-primary-600" />,
      title: "Educational Institutions",
      description: "Universities, colleges, and educational organizations that support our scholarship programs and provide opportunities for our members.",
      partners: ["University of Texas", "Toronto Metropolitan University", "Georgia State University", "Howard University"]
    },
    {
      icon: <FaHeartbeat className="text-3xl text-primary-600" />,
      title: "Healthcare Organizations",
      description: "Medical institutions and health organizations that collaborate on our health outreach programs and medical missions.",
      partners: ["Community Health Centers", "Medical Mission Organizations", "Healthcare Foundations", "Public Health Agencies"]
    },
    {
      icon: <FaRoad className="text-3xl text-primary-600" />,
      title: "Development Partners",
      description: "Organizations and agencies that partner with us on community development projects in our hometown.",
      partners: ["Infrastructure Development Agencies", "Community Development Organizations", "Construction Companies", "Government Agencies"]
    },
    {
      icon: <FaBuilding className="text-3xl text-primary-600" />,
      title: "Corporate Partners",
      description: "Businesses and corporations that provide funding, resources, and opportunities for our community members.",
      partners: ["Tech Companies", "Financial Institutions", "Professional Services Firms", "Manufacturing Companies"]
    }
  ];

  return (
    <div className="w-full bg-white">
      <HeroSection
        title="Our Partners"
        subtitle="Strategic partnerships that amplify our impact and enable us to serve our community more effectively."
        buttons={[
          { text: "Apply Now", link: "/register", variant: "blue" },
          { text: "Login", link: "/login", variant: "blue" }
        ]}
        heroImage={groupEvent}
      />

      {/* Introduction */}
      <section className="section bg-white">
        <div className="section-container">
          <AnimatedSection>
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
                Strategic Partnerships
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                We work with a diverse network of partners across education, healthcare, development, and corporate sectors 
                to maximize our impact and reach.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Partner Categories */}
      <section className="section bg-gray-50">
        <div className="section-container">
          <div className="grid md:grid-cols-2 gap-6">
            {partnerCategories.map((category, index) => (
              <AnimatedSection key={index} delay={0.2 + index * 0.1}>
                <div className="card">
                  <div className="flex items-center gap-3 mb-4">
                    {category.icon}
                    <h3 className="text-xl font-bold text-gray-800">
                      {category.title}
                    </h3>
                  </div>
                  <p className="text-gray-600 mb-4">
                    {category.description}
                  </p>
                  <div className="space-y-2">
                    {category.partners.map((partner, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                        <div className="w-2 h-2 bg-primary-600 rounded-full"></div>
                        <span>{partner}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="section text-center"
        style={{
          backgroundColor: '#F9FAFB'
        }}
      >
        <div className="section-container">
          <AnimatedSection>
            <h2 
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                fontFamily: "'Open Sans', system-ui, sans-serif",
                fontWeight: 700,
                color: '#1F2937'
              }}
            >
              Partner With Us
            </h2>
            <p 
              className="text-lg max-w-2xl mx-auto mb-8"
              style={{
                fontFamily: "'Open Sans', system-ui, sans-serif",
                fontWeight: 400,
                color: '#374151',
                lineHeight: 1.6
              }}
            >
              Interested in partnering with us? Let's explore how we can work together to create positive change.
            </p>
            <Link 
              to="/get-involved/corporate-donor" 
              className="inline-flex items-center justify-center gap-2 px-8 py-3 font-semibold rounded-md transition-colors"
              style={{
                fontFamily: "'Open Sans', system-ui, sans-serif",
                fontWeight: 600,
                backgroundColor: '#1E40AF',
                color: '#FFFFFF',
                textDecoration: 'none',
                borderRadius: '6px'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#1E3A8A';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#1E40AF';
              }}
            >
              Apply Now
              <FaArrowRight />
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
